import React from 'react'
import { makeStyles } from '@material-ui/core'
import { Link, MessagesButton, Tooltip, Typography, VaultButton, Icon, IconButton } from 'components'
import TableCell from '@material-ui/core/TableCell'
import classNames from 'classnames'
import { colors, CONSTANTS } from 'constants/index'
import moment from 'moment-timezone'
import { useDispatch, useSelector } from 'react-redux'
import { navigateToCaseDetails, navigateToEditCase, navigateToProConnectionsInNewTab } from 'store/actions/navigation'
import { goToZendesk } from 'store/actions/common'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'

const useStyles = makeStyles({
  bulletCell: {
    width: 10,
  },
  circle: {
    height: 10,
    width: 10,
    borderRadius: '50%',
    backgroundColor: colors.BASIC.COLOR[20],
  },
  activeBullet: {
    backgroundColor: colors.SUCCESS,
  },
  link: {
    color: colors.BLUE_20,
    textDecoration: 'none',
  },
  tooltip: {
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
  flatFee: {
    color: colors.PRIMARY.COLOR['50'],
  },
  inactiveCase: {
    color: colors.BASIC.COLOR[20],
  },
  feeType: {
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  messageBtn: {
    marginLeft: 8,
  },
  shareButton: {
    width: 16,
    height: 16,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  shareButtonRotated: {
    transform: 'rotate(180deg)',
  },
})

const CASE_STATUS_CONFIG = {
  active: {
    label: 'Active',
    color: 'activeStatus',
    tooltip: 'Case is active and can be worked on',
  },
  inactive: {
    label: 'Inactive',
    color: 'inactiveStatus',
    tooltip: 'Case is inactive and cannot be worked on',
  },
  pending: {
    label: 'Pending',
    color: 'pendingStatus',
    tooltip: 'Case is pending approval or processing',
  },
  rejected: {
    label: 'Rejected',
    color: 'rejectedStatus',
    tooltip: 'Case has been rejected',
  },
  stopped: {
    label: 'Stopped',
    color: 'stoppedStatus',
    tooltip: 'Case has been stopped',
  },
}

const TooltipText = ({ goToZendesk }) => (
  <span>
    You can not send messages until your license is approved. Please contact <Link onClick={goToZendesk}>XIRA</Link>{' '}
    admin for details.
  </span>
)

const ProCaseTableRow = ({
  data,
  timezone,
  profileIsApproved,
  isAccountant = false,
  onShareClick,
  onViewSharedClick,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const feeTypes = CONSTANTS.FEE_TYPES()
  const partiallyFlat = !data.is_flat_fee_case_fully_paid && data.fee_type === 1
  const feeTypeValue = (feeTypes[data.fee_type] && feeTypes[data.fee_type].value) || ''

  const getFeeTypeLabel = () => {
    if (partiallyFlat) {
      return 'Flat fee partially scheduled'
    }
    const baseLabel = feeTypes[data.fee_type] && feeTypes[data.fee_type].label
    if (baseLabel && data.fee_type === 3 && isAccountant) {
      return baseLabel.replace(' case', '')
    }
    return baseLabel || ''
  }

  const feeTypeLabel = getFeeTypeLabel()

  // Determine if project is shared by me or shared with me
  const isSharedByMe = data.is_owner && data.is_shared
  const isSharedWithMe = data.is_shared && !data.is_owner

  const getShareTooltip = () => {
    if (isSharedByMe) {
      return `${isAccountant ? 'Project' : 'Case'} shared by you with ${data.shared_with_count || 0} provider(s)`
    } else if (isSharedWithMe && data.shared_by) {
      return `Shared with you by ${data.shared_by}`
    }
    return ''
  }

  const handleShareIconClick = () => {
    if (isSharedByMe && onShareClick) {
      onShareClick(data)
    } else if (isSharedWithMe && onViewSharedClick) {
      onViewSharedClick(data)
    }
  }

  const navigateToDetails = () => {
    dispatch(navigateToCaseDetails(data.uuid))
  }

  const navigateToConnections = () => {
    dispatch(
      navigateToProConnectionsInNewTab({
        clientName: data.client_name,
      })
    )
  }

  const clientName = data.is_client_deleted ? (
    <Typography variant="body2">{data.client_name}</Typography>
  ) : (
    <Link className={classes.link} onClick={navigateToConnections}>
      {data.client_name}
    </Link>
  )

  const zendeskSso = () => {
    dispatch(goToZendesk())
  }

  const getDisabledMessageTooltip = () => {
    if (!profileIsApproved) {
      return <TooltipText goToZendesk={zendeskSso} />
    } else if (!data.is_ready_for_confidential) {
      return CONSTANTS.DISABLED_MESSAGES_TOOLTIP
    } else if (!data.is_vault_or_chat_available) {
      return CONSTANTS.DISABLED_MESSAGES_INVITED_TOOLTIP
    }
  }

  return (
    <>
      <TableCell className={classes.bulletCell}>
        <Tooltip
          tooltipClass={classes.tooltip}
          childrenClass={classNames({ [classes.flatFee]: partiallyFlat })}
          title={CASE_STATUS_CONFIG[data.status]?.label}
          placement="bottom-start"
        >
          <div
            className={classNames({
              [classes.circle]: true,
              [classes.activeBullet]: data.status === 'active',
            })}
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        <div className={classes.btnContainer}>
          <Link className={classes.link} onClick={navigateToDetails}>
            {data.name}
          </Link>
          {((data.is_owner && data.is_shared) || isSharedWithMe) && (
            <Tooltip tooltipClass={classes.tooltip} title={getShareTooltip()} placement="bottom-start">
              <ShareIcon
                className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`}
                onClick={handleShareIconClick}
              />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>{clientName}</TableCell>
      <TableCell>
        <Tooltip
          tooltipClass={classes.tooltip}
          childrenClass={classNames({ [classes.flatFee]: partiallyFlat })}
          title={feeTypeLabel}
          placement="bottom-start"
        >
          {feeTypeValue}
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2" className={classNames({ [classes.inactiveCase]: data.status === 'inactive' })}>
          {data.practice_area_name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" className={classNames({ [classes.inactiveCase]: data.status === 'inactive' })}>
          {moment(data.updated_at)
            .tz(timezone)
            .format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)}
        </Typography>
      </TableCell>
      <TableCell>
        <div className={classes.btnContainer}>
          <VaultButton
            isInitialCase={data.fee_type === feeTypes['0'].key}
            options={
              data?.is_shared && !isSharedByMe
                ? {
                    userID: data.client,
                    caseID: data.uuid,
                    sharedBy: data.main_pro_uuid,
                  }
                : { userID: data.client, caseID: data.uuid }
            }
            vaultPlace="cases"
          />
          <MessagesButton
            className={classes.messageBtn}
            chatId={data ? data.twilio_chat_sid : ''}
            disabledText={getDisabledMessageTooltip()}
            disabled={
              !profileIsApproved ||
              !data.is_ready_for_confidential ||
              !data.is_vault_or_chat_available ||
              !data.twilio_chat_sid
            }
            place="cases"
          />
        </div>
      </TableCell>
    </>
  )
}

export default ProCaseTableRow
